import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuthenticatedMutation } from '../../hooks/useAuthenticatedQuery';
import {
  GET_EMPLOYEE,
  CREATE_EMPLOYEE,
  UPDATE_EMPLOYEE,
  LIST_EMPLOYEES,
} from "../../graphql/operations";
import { EmployeeInput, Employee } from "../../types/employee"; // Import both types
import { Helmet } from "react-helmet-async";
import CertificationSection from "./CertificationSection";

const EmployeeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [employeeId] = useState<string | null>(id || null);
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState<EmployeeInput>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    position: "",
    department: "",
  });

  // Fetch employee data for editing
  const { data: employeeData, loading: queryLoading, error: queryError } = useQuery(GET_EMPLOYEE, {
    variables: { id },
    skip: !isEdit,
    onError: (error) => {
      console.error("Error fetching employee:", error);
      setError("Failed to load employee data");
    }
  });

  const [createEmployee] = useAuthenticatedMutation(CREATE_EMPLOYEE, {
    refetchQueries: [
      { query: LIST_EMPLOYEES },
    ],
  });

  const [updateEmployee] = useAuthenticatedMutation(UPDATE_EMPLOYEE, {
    refetchQueries: [
      { query: LIST_EMPLOYEES },
    ],
  });

  // Populate form when employee data is loaded - EXCLUDE certifications field
  useEffect(() => {
    if (employeeData?.getEmployee) {
      const employee: Employee = employeeData.getEmployee;
      
      // Only set the fields that are in EmployeeInput (exclude certifications, id, createdAt, updatedAt)
      setFormData({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        phone: employee.phone || "",
        address: employee.address || "",
        position: employee.position || "",
        department: employee.department || "",
      });
    }
  }, [employeeData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEdit) {
        await updateEmployee({
          variables: {
            id: employeeId,
            input: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              address: formData.address,
              position: formData.position,
              department: formData.department,
            },
          },
        });
      } else {
        await createEmployee({
          variables: { 
            input: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              address: formData.address,
              position: formData.position,
              department: formData.department,
            }
          },
        });
      }
      
      // Navigate after successful mutation
      navigate("/dashboard/employees");
    } catch (error: any) {
      console.error("Error saving employee:", error);
      setError(error.message || "Failed to save employee");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EmployeeInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  if (queryLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading employee: {queryError.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>
          Almond Tree Dashboard - {isEdit ? "Update" : "Create"} Employee
        </title>
      </Helmet>
      
      <Typography variant="h4" gutterBottom>
        {isEdit ? "Edit Employee" : "Add Employee"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleChange('phone')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleChange('address')}
                required
                multiline
                rows={3}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={formData.position}
                onChange={handleChange('position')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={handleChange('department')}
                required
                disabled={loading}
              />
            </Grid>
          </Grid>

          {isEdit && employeeId && (
            <CertificationSection employeeId={employeeId} isEdit={isEdit} />
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ backgroundColor: "#919749" }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Saving..." : (isEdit ? "Update" : "Create")} Employee
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="large"
              onClick={() => navigate("/dashboard/employees")}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmployeeForm;